import * as Sentry from "@sentry/react";
import type { Locale } from "../../../shared/constants/locales";

declare global {
  interface Window {
    dataLayer?: unknown[];
  }
}

// Interim sign-up conversion tracking (pre-CAPI).
//
// GTM (GTM-W7SJKHGV, loaded in index.html) runs on the app domain with
// consent-mode marketing/statistics defaulting to denied — the Complianz
// banner only runs on the WordPress marketing site — so Sign Up tags fire
// without delivering any signal. The fix: replay the user's actual
// Complianz consent choice on the app domain, then push a single
// `sign_up_completed` dataLayer event for GTM to act on.
//
// Registration ends in `window.location.reload()` (see Register.tsx),
// which would abort the tags' network requests if the event fired there.
// The register page therefore only records the pending market in
// sessionStorage, and the dashboard flushes it after the reload — exactly
// once per account creation (the flag is consumed on flush, so dashboard
// re-loads and re-logins never fire it again).

// Mirrors `mapLanguageToCountry` in server/src/mappings/language-country.ts:
// the server derives the new account's country from the registration
// language, so this is the market the account belongs to.
const REGISTRATION_MARKET_BY_LANGUAGE: Record<Locale, string> = {
  sl: "SI",
  en: "US",
  de: "DE",
  hr: "HR",
  it: "IT",
  es: "ES",
  sr: "RS",
  fr: "FR",
  bs: "BA",
};

const PENDING_SIGNUP_MARKET_KEY = "pending_signup_market";

export function getMarketForRegistrationLanguage(language: Locale): string {
  return REGISTRATION_MARKET_BY_LANGUAGE[language];
}

/**
 * Parses the user's Complianz consent choice out of a `document.cookie`
 * string. Complianz stores each category as `cmplz_<category>` with the
 * value "allow" when granted; anything else — including a missing cookie,
 * e.g. when the user arrived from a different registrable domain such as
 * lime-booking.si — counts as denied.
 */
export function parseComplianzConsent(cookieString: string): {
  marketing: boolean;
  statistics: boolean;
} {
  function readCookie(name: string): string | null {
    const match = cookieString.match("(^|;)\\s*" + name + "\\s*=\\s*([^;]+)");
    return match ? decodeURIComponent(match[2]) : null;
  }

  return {
    marketing: readCookie("cmplz_marketing") === "allow",
    statistics: readCookie("cmplz_statistics") === "allow",
  };
}

// GTM's consent API only processes real `arguments` objects pushed to the
// dataLayer — a plain array is silently ignored.
function gtag(..._args: unknown[]): void {
  // eslint-disable-next-line prefer-rest-params
  window.dataLayer?.push(arguments);
}

/**
 * Records that an account was just created so the dashboard can fire the
 * `sign_up_completed` tracking event after the post-registration reload.
 * Never throws — tracking must not break registration.
 */
export function markSignupCompleted(language: Locale): void {
  try {
    sessionStorage.setItem(
      PENDING_SIGNUP_MARKET_KEY,
      getMarketForRegistrationLanguage(language),
    );
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: "signup-tracking" } });
  }
}

/**
 * Fires the `sign_up_completed` GTM event for a registration recorded by
 * `markSignupCompleted`, replaying the user's Complianz consent choice
 * first — the consent update must precede the event push, otherwise the
 * tags fire while consent is still denied and deliver nothing. The consent
 * update reflects the user's actual banner choice, never a blanket grant.
 * No-op when no registration is pending; the pending flag is consumed up
 * front so the event fires at most once. Never throws — tracking must not
 * break the dashboard.
 */
export function flushPendingSignupTracking(): void {
  try {
    const market = sessionStorage.getItem(PENDING_SIGNUP_MARKET_KEY);
    if (!market) return;
    sessionStorage.removeItem(PENDING_SIGNUP_MARKET_KEY);

    window.dataLayer = window.dataLayer || [];

    const consent = parseComplianzConsent(document.cookie);
    gtag("consent", "update", {
      ad_storage: consent.marketing ? "granted" : "denied",
      ad_user_data: consent.marketing ? "granted" : "denied",
      ad_personalization: consent.marketing ? "granted" : "denied",
      analytics_storage: consent.statistics ? "granted" : "denied",
    });

    window.dataLayer.push({ event: "sign_up_completed", market });
  } catch (error) {
    Sentry.captureException(error, { tags: { feature: "signup-tracking" } });
  }
}
